import { useEffect, useState, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';

// Plan 67b: short-lived presigned URLs for Notes image attachments.
// The oakleaf-images bucket is private; the device-data-api signs each
// GET on demand. URLs expire after 60 minutes (server-side); the cache
// keeps them slightly shorter to avoid serving a URL that expires mid-load.
// Notes can hold multiple images, so cache entries key on noteId + imageId.

interface CacheEntry {
  url: string;
  expiresAt: number;
}

const SAFETY_MARGIN_MS = 5 * 60 * 1000;
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<CacheEntry>>();

async function fetchAttachmentUrl(noteId: number, imageId: number): Promise<CacheEntry> {
  const res = await axiosInstance.get<{ url: string; expiresAt: string }>(
    `${config.notesApiUrl}/notes/${noteId}/attachments/${imageId}/url`,
  );
  return { url: res.data.url, expiresAt: Date.parse(res.data.expiresAt) };
}

async function getOrFetch(noteId: number, imageId: number, force = false): Promise<CacheEntry> {
  const key = `${noteId}:${imageId}`;
  const now = Date.now();
  const cached = cache.get(key);
  if (!force && cached && cached.expiresAt - now > SAFETY_MARGIN_MS) {
    return cached;
  }
  let pending = inflight.get(key);
  if (!pending || force) {
    pending = fetchAttachmentUrl(noteId, imageId)
      .then((entry) => {
        cache.set(key, entry);
        return entry;
      })
      // Clear inflight on failure too — a rejected promise left in the map
      // would permanently poison every later fetch for this image.
      .finally(() => {
        if (inflight.get(key) === pending) inflight.delete(key);
      });
    inflight.set(key, pending);
  }
  return pending;
}

export function useNoteAttachmentUrl(
  noteId: number | null | undefined,
  imageId: number | null | undefined,
): {
  url: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef(false);

  const load = useCallback(
    (force: boolean) => {
      if (noteId == null || imageId == null) {
        setUrl(null);
        return;
      }
      cancelRef.current = false;
      setLoading(true);
      setError(null);
      getOrFetch(noteId, imageId, force)
        .then((entry) => {
          if (cancelRef.current) return;
          setUrl(entry.url);
        })
        .catch((err) => {
          if (cancelRef.current) return;
          setError(err?.message || 'Failed to load attachment');
          setUrl(null);
        })
        .finally(() => {
          if (!cancelRef.current) setLoading(false);
        });
    },
    [noteId, imageId],
  );

  useEffect(() => {
    load(false);
    return () => {
      cancelRef.current = true;
    };
  }, [load]);

  const refetch = useCallback(() => {
    if (noteId != null && imageId != null) cache.delete(`${noteId}:${imageId}`);
    load(true);
  }, [load, noteId, imageId]);

  return { url, loading, error, refetch };
}
